import { useState } from "react";
import { Info } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { galleryCategories, galleryItems, images } from "@/data/site";

export default function Gallery() {
  const [active, setActive] = useState("All");
  const filtered =
    active === "All" ? galleryItems : galleryItems.filter((i) => i.category === active);

  return (
    <>
      <Seo
        title="Project Gallery | Future Roofing and Repair — Bucks County, PA"
        description="Browse roofing project examples by category: residential, commercial, repairs, metal, flat roofing and before & after. Bucks County, PA roofing company."
        keywords="Roofing gallery Bucks County, Roofing projects Bristol PA, Before and after roof, Metal roofing photos"
      />
      <PageHero
        kicker="Our Work"
        title="Project Gallery"
        subtitle="A look at the kinds of roofing projects we take on across Bucks County. Real project photos coming soon."
        image={images.residential}
      />

      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          {/* Placeholder notice */}
          <div
            className="flex items-start gap-3 bg-gold/40 border border-charcoal/20 p-4 mb-8"
            data-testid="gallery-placeholder-notice"
          >
            <Info className="w-5 h-5 text-charcoal mt-0.5 shrink-0" />
            <p className="text-sm text-charcoal/85 leading-relaxed">
              <strong className="font-semibold">Note:</strong> The images below are professional
              placeholder photos shown as examples by category. They will be replaced with Future
              Roofing and Repair's real project photos once uploaded.
            </p>
          </div>

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
            {galleryCategories.map((cat) => (
              <button
                key={cat}
                data-testid={`gallery-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setActive(cat)}
                className={`font-accent uppercase text-sm tracking-wide px-4 py-2 border border-charcoal/20 transition-colors ${
                  active === cat
                    ? "bg-charcoal text-cream"
                    : "bg-cream text-charcoal hover:bg-cream-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
            {filtered.map((item, idx) => (
              <figure
                key={`${item.src}-${idx}`}
                className="group relative border border-charcoal/20 shadow-md overflow-hidden bg-charcoal"
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  loading="lazy"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 bg-rust text-cream font-accent uppercase text-[10px] tracking-widest px-2 py-1 border border-charcoal">
                  Placeholder
                </span>
                <figcaption className="absolute bottom-0 left-0 right-0 bg-charcoal/85 text-cream font-accent uppercase text-xs tracking-wide px-3 py-2">
                  {item.category}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        variant="default"
        kicker="See Something You Like?"
        title="Let's Talk About Your Project"
        text="Request a free estimate and we'll help you plan your roofing project from start to finish."
      />
    </>
  );
}
